import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { DateTerm } from '../../../../utils/date'
import uiStates, {
  UiStateKey,
  UiStateScope,
} from '../../../../lib/commons/uiStates'
import DateVO from '../../../../vo/DateVO'
import { SearchUnit } from '../../../components/headers/HeaderBar/DateRangeSelector'
import { ROW_HEIGHT } from '../../../containers/BulkSheet'
import { DecimalAndSexagesimalScale } from '../../../../lib/functions/decimalAndSexagesimalScale'
import { EndDatePreset } from '../../../pages/ProjectTaskActualWorkApprovalStatus/Header/EndDateSelector'

const checkIsCurrentMonth = (year: number, month: number): boolean => {
  const now = DateVO.now()
  return now.getYear() === year && now.getMonth() === month
}

export const calculateEndDateFromPreset = (
  preset: EndDatePreset | undefined,
  fallbackDate: DateVO
): DateVO => {
  const now = DateVO.now()

  if (preset === EndDatePreset.YESTERDAY) {
    return now.addDays(-1)
  } else if (preset === EndDatePreset.TODAY) {
    return now
  } else {
    return fallbackDate
  }
}

type StoredTaskActualWorkApprovalStatusTenantPageState = {
  searchUnit: SearchUnit
  dateTerm: DateTerm
  displayDateTerm?: DateTerm
  rowHeight: ROW_HEIGHT
  decimalScaleUnit?: DecimalAndSexagesimalScale
  endDate?: string
  endDatePreset?: EndDatePreset
}

export const usePageState = ({ functionUuid }: { functionUuid: string }) => {
  const now = DateVO.now()
  const [decimalScaleUnit, setDecimalScaleUnit] =
    useState<DecimalAndSexagesimalScale>(DecimalAndSexagesimalScale.DECIMAL)
  const [searchUnit, setSearchUnit] = useState<SearchUnit>(SearchUnit.MONTH)
  const [rowHeight, setRowHeight] = useState<ROW_HEIGHT>(ROW_HEIGHT.SMALL)
  const [initialized, setInitialized] = useState(false)
  const [displayDateTerm, setDisplayDateTerm] = useState<DateTerm>({
    startDate: now.getFirstDayOfMonth().format(),
    endDate: now.getLastDayOfMonth().format(),
  })
  const [endDate, setEndDate] = useState<DateVO>(now.getLastDayOfMonth())
  const [endDatePreset, setEndDatePreset] = useState<EndDatePreset>(
    EndDatePreset.END_OF_MONTH
  )
  const dateTerm = useRef<DateTerm>({
    startDate: now.getFirstDayOfMonth().format(),
    endDate: now.getLastDayOfMonth().format(),
  })
  const monthDateTerm = useRef<DateTerm>({
    startDate: now.getFirstDayOfMonth().format(),
    endDate: now.getLastDayOfMonth().format(),
  })

  const storePageState = useCallback(
    async (value: StoredTaskActualWorkApprovalStatusTenantPageState) => {
      uiStates.update(
        {
          key: UiStateKey.PageState,
          scope: UiStateScope.User,
          value: JSON.stringify(value),
        },
        functionUuid
      )
    },
    [functionUuid]
  )

  useEffect(() => {
    if (!initialized) return
    storePageState({
      searchUnit,
      dateTerm: dateTerm.current,
      displayDateTerm,
      rowHeight,
      decimalScaleUnit,
      endDate: endDate.serialize(),
      endDatePreset,
    })
  }, [
    initialized,
    storePageState,
    searchUnit,
    rowHeight,
    displayDateTerm,
    decimalScaleUnit,
    endDate,
    endDatePreset,
  ])

  const restorePageState = useCallback(async () => {
    const response = await uiStates.get({
      applicationFunctionUuid: functionUuid,
      key: UiStateKey.PageState,
      scope: UiStateScope.User,
    })
    const stored = response.json
    try {
      if (!stored.value) {
        setInitialized(true)
        return
      }
      const state = JSON.parse(stored.value)
      state.dateTerm && (dateTerm.current = state.dateTerm)
      if (state.displayDateTerm) {
        setDisplayDateTerm(state.displayDateTerm)
        monthDateTerm.current = state.displayDateTerm

        const displayDate = new DateVO(state.displayDateTerm.startDate)
        const isCurrentMonth = checkIsCurrentMonth(
          displayDate.getYear(),
          displayDate.getMonth()
        )

        if (state.endDatePreset) {
          setEndDatePreset(state.endDatePreset)
        }

        if (isCurrentMonth) {
          if (state.endDate) {
            setEndDate(
              calculateEndDateFromPreset(
                state.endDatePreset,
                now.getLastDayOfMonth()
              )
            )
          } else {
            setEndDate(now.getLastDayOfMonth())
          }
        } else {
          const lastDayOfMonth = new DateVO(state.displayDateTerm.endDate)
          setEndDate(lastDayOfMonth)
        }
      } else {
        setEndDate(now.getLastDayOfMonth())
      }
      state.searchUnit && setSearchUnit(state.searchUnit)
      state.rowHeight && setRowHeight(state.rowHeight)
      state.decimalScaleUnit && setDecimalScaleUnit(state.decimalScaleUnit)
    } finally {
      setInitialized(true)
    }
  }, [functionUuid])

  useEffect(() => {
    restorePageState()
  }, [restorePageState])

  const monthRange = useMemo(() => {
    if (!initialized) {
      const now = DateVO.now()
      return {
        startDateString: now.getFirstDayOfMonth().serialize(),
        endDateString: now.getLastDayOfMonth().serialize(),
      }
    }
    const startDate = new DateVO(displayDateTerm.startDate)
    const endDate = new DateVO(displayDateTerm.endDate)
    return {
      startDateString: startDate.serialize(),
      endDateString: endDate.serialize(),
    }
  }, [initialized, displayDateTerm])

  const handleMonthChange = useCallback(
    (year: number, month: number) => {
      const startDate = new DateVO(
        `${year}-${String(month).padStart(2, '0')}-01`
      )
      const nextMonth = month === 12 ? 1 : month + 1
      const nextYear = month === 12 ? year + 1 : year
      const nextMonthFirstDay = new DateVO(
        `${nextYear}-${String(nextMonth).padStart(2, '0')}-01`
      )
      const lastDayOfMonth = nextMonthFirstDay.addDays(-1)
      const newDateTerm = {
        startDate: startDate.serialize(),
        endDate: lastDayOfMonth.serialize(),
      }
      monthDateTerm.current = newDateTerm
      setDisplayDateTerm(newDateTerm)

      const isCurrentMonth = checkIsCurrentMonth(year, month)

      if (isCurrentMonth) {
        setEndDate(calculateEndDateFromPreset(endDatePreset, lastDayOfMonth))
      } else {
        setEndDate(lastDayOfMonth)
      }
    },
    [endDatePreset]
  )

  const isCurrentMonth = useMemo(() => {
    if (!initialized) return true
    const displayDate = new DateVO(displayDateTerm.startDate)
    return checkIsCurrentMonth(displayDate.getYear(), displayDate.getMonth())
  }, [initialized, displayDateTerm.startDate])

  return {
    initialized,
    searchUnit,
    rowHeight,
    setRowHeight,
    displayDateTerm,
    setDisplayDateTerm,
    monthRange,
    handleMonthChange,
    decimalScaleUnit,
    setDecimalScaleUnit,
    endDate,
    setEndDate,
    isCurrentMonth,
    endDatePreset,
    setEndDatePreset,
  }
}
